<template>
    <div class="base-layout">
        <slot v-if="fullBleedHeader" name="header" />
        <div class="base-layout-gutters">
            <slot v-if="!fullBleedHeader" name="header" />
            <div class="base-layout-content" :class="[widthClasses[width], gapClasses[gap], !noTopPadding && 'base-layout-content-padded', fillHeight && 'grow', contentClass]">
                <slot />
            </div>
        </div>
    </div>
</template>

<script lang="ts">
export type BaseLayoutWidth = "narrow" | "default" | "full";
export type BaseLayoutGap = "none" | "sm" | "md" | "lg" | "xl";

interface BaseLayoutProps {
    // Max width of the centered content column.
    //   narrow  — reading column for long-form content (reports, results)
    //   default — standard page column
    //   full    — spans the gutters; for boards, wide tables, dashboards
    width?: BaseLayoutWidth;
    // Vertical rhythm between the top-level sections of the page. The content
    // column is always a flex column, so this is the gap between its children.
    // Use "none" for pages that space their own sections.
    gap?: BaseLayoutGap;
    // Stretches the content column to fill the viewport height. Use for pages
    // that vertically center their content or pin something to the bottom.
    fillHeight?: boolean;
    // Drops the standard top padding. Use when the page renders its own
    // spacing above the first element (e.g. a header slot that already pads).
    noTopPadding?: boolean;
    // Renders the header slot outside the side gutters so it spans the full
    // width of the page area. Use for edge-to-edge sticky bars (e.g. report
    // action bars); leave off for headers that should align with the gutters
    // (back links, content-aligned buttons).
    fullBleedHeader?: boolean;
    // Escape hatch for one-off layout on the content column (e.g.
    // "items-center pt-[15vh]" for an intro screen). Prefer the props above —
    // if you reach for the same classes twice, make it a variant instead.
    // Note the column isn't the component root, so classes passed via
    // `class="..."` land on the outer wrapper rather than here.
    contentClass?: string;
}
</script>

<script setup lang="ts">
const { width = "default", gap = "md", noTopPadding = false, fillHeight = false, fullBleedHeader = false } = defineProps<BaseLayoutProps>();

const widthClasses: Record<BaseLayoutWidth, string> = {
    narrow: "max-w-190",
    default: "max-w-225",
    full: "max-w-none",
};

const gapClasses: Record<BaseLayoutGap, string> = {
    none: "",
    sm: "gap-4 md:gap-6",
    md: "gap-6",
    lg: "gap-10 md:gap-10",
    xl: "gap-10",
};
</script>

<style scoped>
@reference "../../../css/tailwind.css";

@layer components {
    .base-layout {
        @apply flex grow flex-col bg-surface;
    }

    /* Page gutters live on this wrapper from md up; on mobile they live on the
       content column instead (px-6 below) so pages can bleed full-width
       elements with -mx-6, e.g. the homepage's sticky tabs list. */
    .base-layout-gutters {
        @apply flex grow flex-col px-0 md:px-6 lg:px-10;
    }

    .base-layout-content {
        @apply mx-auto flex w-full flex-col px-6 pb-10 md:px-0;
    }

    .base-layout-content-padded {
        @apply pt-6 md:pt-10;
    }
}
</style>
